/**
 * Piezas visuales compartidas entre pantallas.
 */
import type { Semaforo } from '@matias-asset-os/contracts';
import { formatearDia } from '@matias-asset-os/i18n';

/** Color del punto de semáforo, para pintarlo inline sobre foto o en leyendas. */
export const COLOR_SEMAFORO: Record<Semaforo, string> = {
  VERDE: 'var(--verde)',
  AMARILLO: 'var(--ambar)',
  ROJO: 'var(--rojo)',
};

/** Clase de la pastilla de semáforo (la maqueta usa minúsculas de color). */
export const CLASE_SEMAFORO: Record<Semaforo, string> = {
  VERDE: 'verde',
  AMARILLO: 'ambar',
  ROJO: 'rojo',
};

/** Pastilla de nivel operativo (NIO). El 4 va en rojo; sobre foto, superpuesta. */
export function ChipNio({ nivel, sobreFoto = false }: { nivel: number | null; sobreFoto?: boolean }) {
  if (nivel === null) return null;
  if (sobreFoto) {
    return <span className={`sobre-foto${nivel >= 4 ? ' nio-alto' : ''}`}>NIO {nivel}</span>;
  }
  return <span className={`nio nivel-${nivel}`}>NIO {nivel}</span>;
}

/**
 * Deriva una semilla numérica estable a partir de un texto (el id o el código de
 * la propiedad). Misma propiedad, mismo dibujo, siempre.
 */
export function semillaDe(texto: string): number {
  let h = 2166136261;
  for (let i = 0; i < texto.length; i += 1) {
    h ^= texto.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

/**
 * El hueco de la foto que todavia no existe.
 *
 * ANTES ERA UNA VILLA DIBUJADA: mar, montanas, cipreses y piscina. Se veia
 * bonita en una maqueta y mal en un producto, porque una casa dibujada que no
 * es la casa del cliente es una mentira pequena repetida en cada tarjeta —y el
 * dueno de la casa es justo quien mas nota que ese no es su jardin.
 *
 * Lo que va aqui es un hueco declarado: un fondo sobrio con el tono sembrado
 * por la propia propiedad —para que la cartera no sea una pared de rectangulos
 * identicos—, la silueta de una casa a un pixel y, si se pide, el rotulo que
 * dice lo que es. El dia que hay foto real, la foto ocupa este sitio sin tocar
 * nada mas.
 */
export function FotoVilla({
  semilla,
  alto = 170,
  etiqueta = true,
  variante = 'normal',
}: {
  semilla: number;
  alto?: number;
  etiqueta?: boolean;
  variante?: 'normal' | 'ancha';
}) {
  const id = `v${semilla}`;
  const vh = variante === 'ancha' ? 78 : 200;

  /*
    El tono se saca de la semilla dentro de una banda estrecha —del azul de
    pizarra al verde oliva apagado—, nunca del circulo entero: doce tarjetas
    seguidas con doce colores distintos serian un muestrario, no una cartera.
  */
  const tono = 186 + (semilla % 62);
  const claro = `hsl(${tono} 14% 88%)`;
  const oscuro = `hsl(${tono} 16% 74%)`;
  const trazo = `hsl(${tono} 20% 46%)`;

  return (
    // `span` en bloque, no `div`: la tarjeta de la cartera es un `button` y el
    // contenido de un botón debe ser «phrasing content». Así la foto puede vivir
    // dentro de la tarjeta sin HTML inválido.
    <span className="foto foto-pendiente" style={{ height: alto, display: 'block' }}>
      <svg viewBox={`0 0 400 ${vh}`} preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <defs>
          <linearGradient id={`${id}f`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={claro} />
            <stop offset="100%" stopColor={oscuro} />
          </linearGradient>
        </defs>
        <rect width="400" height={vh} fill={`url(#${id}f)`} />
        {/* La casa, a un trazo. Centrada y pequena: es una marca, no un dibujo. */}
        <g
          fill="none"
          stroke={trazo}
          strokeWidth="1.5"
          strokeLinejoin="round"
          opacity="0.5"
          transform={`translate(200 ${vh / 2}) scale(${variante === 'ancha' ? 0.8 : 1})`}
        >
          <path d="M-22 2 L0 -16 L22 2" />
          <path d="M-16 0 L-16 18 L16 18 L16 0" />
          <path d="M-5 18 L-5 7 L5 7 L5 18" />
        </g>
      </svg>
      {etiqueta && <span className="marca-agua">Imagen pendiente</span>}
    </span>
  );
}

/**
 * Una incidencia sigue pesando mientras esta abierta o en curso.
 *
 * Vive aqui y no en cada pantalla porque el sistema entero responde a esta
 * misma pregunta —el semaforo, las alertas, los conteos, los filtros— y basta
 * con que una pantalla se olvide de «en curso» para que esconda justo las
 * incidencias que alguien esta atendiendo. El servidor tiene su gemela.
 */
export function incidenciaActiva(estado: string): boolean {
  return estado === 'OPEN' || estado === 'IN_PROGRESS';
}

/**
 * Una fecha ISO escrita como la escribiria una persona, en el idioma elegido.
 *
 * `2026-09-10` es correcto y no se lee: obliga a descifrar el mes cada vez y,
 * escrito en un dato grande, parte el titular en dos lineas. Se fija la zona a
 * UTC porque estas fechas son dias del calendario, no instantes: sin fijarla,
 * un navegador al oeste de Greenwich muestra la vispera.
 */
/*
  UNA SOLA FORMA DE ESCRIBIR UNA FECHA: 11-09-2026.

  Esto escribia «11 sept 2026», que se lee bien pero no es lo que hace el resto
  de la aplicacion. Ahora delega en `formatearDia`, que es el unico sitio donde
  vive el formato. El parametro de idioma se queda en la firma —lo pasan
  decenas de llamadas— y ya no decide nada: el formato es el mismo para todos.
*/
export function fechaLarga(iso: string | null | undefined, _idioma = 'es'): string {
  return formatearDia(iso);
}
